import type { CaseReducer, PayloadAction } from '@reduxjs/toolkit';
import type { HomeReview, ReviewResponse, ReviewState } from '../../types';

type UpdateLocalResponsePayload = {
  reviewId: number;
  itemId: number;
  data: Partial<ReviewResponse>;
};

const setActiveReview: CaseReducer<ReviewState, PayloadAction<HomeReview>> = (state, action) => {
  state.activeReview = action.payload;
};

const clearActiveReview: CaseReducer<ReviewState> = (state) => {
  state.activeReview = null;
};

const updateLocalResponse: CaseReducer<ReviewState, PayloadAction<UpdateLocalResponsePayload>> = (
  state,
  action
) => {
  const { reviewId, itemId, data } = action.payload;

  // Find and update review in state.reviews array (primary source of truth)
  const reviewInArray = state.reviews.find(r => r.id === reviewId);
  if (reviewInArray) {
    if (!reviewInArray.responses) {
      reviewInArray.responses = [];
    }

    const responseInArray = reviewInArray.responses.find((r: any) => r.itemId === itemId);
    if (responseInArray) {
      // Deep merge responseData if it exists in the update
      const mergedResponseData = data.responseData
        ? { ...responseInArray.responseData, ...data.responseData }
        : responseInArray.responseData;

      Object.assign(responseInArray, data, {
        responseData: mergedResponseData,
        updatedAt: new Date().toISOString(),
      });
    } else {
      // Create a new response if it doesn't exist (optimistic update)
      const newResponse: ReviewResponse = {
        id: -1, // Temporary ID
        reviewId: reviewInArray.id,
        itemId: itemId,
        isPresent: true,
        status: data.status || 'not_started',
        responseData: {},
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        ...data,
      } as ReviewResponse;
      reviewInArray.responses.push(newResponse);
    }

    // Update review status to in_progress when response added with non-not_started status
    if (data.status && data.status !== 'not_started') {
      if (reviewInArray.status === 'draft') {
        reviewInArray.status = 'in_progress';
      }
    }
  }

  // ALSO update activeReview if it matches (for backwards compatibility)
  if (state.activeReview && state.activeReview.id === reviewId) {
    if (!state.activeReview.responses) {
      state.activeReview.responses = [];
    }

    const activeResponse = state.activeReview.responses.find((r: any) => r.itemId === itemId);
    if (activeResponse) {
      // Deep merge responseData if it exists in the update
      const mergedResponseData = data.responseData
        ? { ...activeResponse.responseData, ...data.responseData }
        : activeResponse.responseData;

      Object.assign(activeResponse, data, {
        responseData: mergedResponseData,
        updatedAt: new Date().toISOString(),
      });
    } else {
      // Create a new response if it doesn't exist
      const newResponse: ReviewResponse = {
        id: -1, // Temporary ID
        reviewId: state.activeReview.id,
        itemId: itemId,
        isPresent: true,
        status: data.status || 'not_started',
        responseData: {},
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        ...data,
      } as ReviewResponse;
      state.activeReview.responses.push(newResponse);
    }

    // Update activeReview status to in_progress
    if (data.status && data.status !== 'not_started') {
      if (state.activeReview.status === 'draft') {
        state.activeReview.status = 'in_progress';
      }
    }
  }
};

const setError: CaseReducer<ReviewState, PayloadAction<string | null>> = (state, action) => {
  state.error = action.payload;
};

export const reviewCaseReducers = {
  setActiveReview,
  clearActiveReview,
  updateLocalResponse,
  setError,
};
